import {
  AmsVerdictOutcome,
  type AmsFieldVerdictFieldsFragment,
  type AmsItemDisplay,
  type AmsSectionDisplay
} from '@oysterjs/ais/api/graphql';

type Verdict = AmsFieldVerdictFieldsFragment;

/**
 * A field's row, or one level of what it sits under as the server placed it: a heading,
 * a card, a list, or one of a list's items, drawn as its display says.
 */
export type ReviewNode =
  | { kind: 'field'; verdict: Verdict }
  | {
      kind: 'level';
      key: string;
      display: AmsSectionDisplay;
      label: string;
      itemDisplay: AmsItemDisplay;
      matchedOn?: string;
      /** Why nothing on an item can be written, said once for the item. */
      readOnlyReason?: string;
      children: ReviewNode[];
    };

export type ReviewLevel = Extract<ReviewNode, { kind: 'level' }>;

/**
 * Nests a destination's verdicts under the levels each one's placement names, in the
 * order they arrived. Two verdicts share a level when they name the same levels above
 * it. An item's card can be made by an inner item's field before its own arrive, so
 * what it matched on and why it cannot be written are taken from whichever field says.
 */
export function groupVerdicts(verdicts: readonly Verdict[]): ReviewNode[] {
  const root: ReviewNode[] = [];
  for (const verdict of verdicts) {
    let children = root;
    let path = '';
    for (const level of verdict.placement) {
      path = `${path}/${level.display}:${level.key}`;
      let node = children.find(
        (child): child is ReviewLevel => child.kind === 'level' && child.key === path
      );
      if (!node) {
        node = {
          kind: 'level',
          key: path,
          display: level.display,
          label: level.label,
          itemDisplay: level.itemDisplay,
          children: []
        };
        children.push(node);
      }
      node.matchedOn ??= level.matchedOn ?? undefined;
      node.readOnlyReason ??= level.readOnlyReason ?? undefined;
      children = node.children;
    }
    children.push({ kind: 'field', verdict });
  }
  return root;
}

/** Every field under a node, in order. */
export function flattenFields(nodes: readonly ReviewNode[]): Verdict[] {
  return nodes.flatMap((node) =>
    node.kind === 'field' ? [node.verdict] : flattenFields(node.children)
  );
}

/**
 * A block opens by default when the document differs from the AMS anywhere in it,
 * writable or not. Outcomes never change during a review, so neither does the default.
 */
export function startsOpen(verdicts: readonly Verdict[]): boolean {
  return verdicts.some(
    (verdict) =>
      verdict.outcome === AmsVerdictOutcome.Differs ||
      verdict.outcome === AmsVerdictOutcome.AbsentFromAms
  );
}
